import { showToast } from "@certn/cascadia";
import { LinkifiedText, Text, TextArea } from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { MutableRefObject, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useSaveTextResponse } from "./hooks";
import { useInformationRequestSaves } from "./InformationRequestSaves";
import { InformationRequestItem, InformationRequestItemAnswer } from "./types";

type Props = {
  caseId: string;
  item: InformationRequestItem;
  isReadOnly?: boolean;
  onAnswered: (answer: InformationRequestItemAnswer) => void;
  /** Called with whether the field holds an answer that may not be saved yet. */
  onDraftChange?: (hasDraft: boolean) => void;
  /** Set to a function that saves the current answer if it isn't saved yet. */
  saveDraftRef?: MutableRefObject<(() => Promise<boolean>) | null>;
};

export function WrittenResponseField({
  caseId,
  item,
  isReadOnly = false,
  onAnswered,
  onDraftChange,
  saveDraftRef,
}: Props) {
  const { t } = useTranslation();
  const { trackSave } = useInformationRequestSaves();
  const saveTextResponse = useSaveTextResponse(caseId, item.id);
  const [value, setValue] = useState(item.text_response ?? "");
  const savedValue = useRef(item.text_response ?? "");
  const confirmedValue = useRef(item.text_response ?? "");
  const lastSave = useRef<Promise<unknown>>(Promise.resolve());
  const failedValue = useRef<string | null>(null);

  const save = (text: string) => {
    savedValue.current = text;
    const pending = lastSave.current
      .catch(() => undefined)
      .then(() => saveTextResponse({ text_response: text }));
    lastSave.current = pending;
    return trackSave(pending).then(
      (answer) => {
        confirmedValue.current = text;
        failedValue.current = null;
        if (answer) {
          onAnswered(answer);
        }
        return true;
      },
      () => {
        failedValue.current = text;
        if (savedValue.current === text) {
          savedValue.current = confirmedValue.current;
        }
        return false;
      }
    );
  };

  const reportFailure = (saved: boolean) => {
    if (!saved) {
      showToast(t("We couldn't save your answer. Please try again."), {
        type: "error",
      });
    }
    return saved;
  };

  const handleBlur = () => {
    if (isReadOnly || value === savedValue.current) {
      return;
    }
    void save(value).then(reportFailure);
  };

  if (saveDraftRef) {
    saveDraftRef.current = async () => {
      await lastSave.current.catch(() => undefined);
      if (value === confirmedValue.current) {
        return true;
      }
      if (value === failedValue.current) {
        failedValue.current = null;
        return false;
      }
      return reportFailure(await save(value));
    };
  }

  if (isReadOnly) {
    return (
      <div className={styles.readOnly}>
        <Text style="input/label">{t("Response")}</Text>
        <Text
          style="global/body-primary"
          className={styles.response}
          testID={`information-request-response-${item.id}`}
        >
          <LinkifiedText
            text={value}
            testID={`information-request-response-${item.id}-link`}
          />
        </Text>
      </div>
    );
  }

  return (
    <TextArea
      label={t("Response")}
      testID={`information-request-response-${item.id}`}
      value={value}
      onChange={(next) => {
        setValue(next);
        onDraftChange?.(next.trim() !== "");
      }}
      onBlur={handleBlur}
      minHeight={80}
      isRequired
    />
  );
}

const styles = {
  readOnly: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-2)",
  }),
  response: css({
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
  }),
};
